import React, {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
  useMemo,
  useContext
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import isEmpty from "lodash/isEmpty";
import {
  usePutIngredientData,
  usePostSupplierData,
  usePutSupplierData
} from "../../../../../api/ingredients";
import { errorView, successMessage } from "../../../../../helpers/ErrorHandler";
import {
  useGetCountries,
  useGetProduceTypes,
  useGetSpecifyIngredient
} from "../../../../../api/static-data";
import SupplierRegisterFields from "../../../../lcaSupplier/shared/components/SupplierRegisterFields";
import SupplierRegisterForm from "../../../../supplier/supplierRegisterForm/SuppllierRegisterForm";
import SupplerIngredientDataView from "./SupplierIngredientDataView";
import "./IngredientRegister.scss";
import { useCreateSupplierDrawingRequest } from "../../../../../api/supplier";
import {
  ENABLE_LCA_SUPPLIER_UI_REDESIGN,
  SUPPLIER_DEFAULT_COUNTRY_GLOBAL,
  THEME
} from "../../../../../config/constants";
import { DEFAULT_UNIT_TYPE } from "../../../../../config/optionsConstants";
import { DrawingContext } from "../../../../../components/drawing-tool/DrawingTool";
import { useReactFlow } from "reactflow";
import {
  IngredientDataType,
  IngredientType
} from "../../../../../config/enums/IngredientType.enum";
import { trim } from "lodash";

const SupplerRegister = forwardRef((props: any, ref) => {
  const { getNode } = useReactFlow();
  const {
    ingredientSupplierList = [],
    supplierCurrentData,
    ingredientData,
    refetchSupplier = () => {},
    setIngredientData = () => {},
    setSupplier = () => {},
    loadPackageData = () => {},
    hideIngredientView = () => {},
    disableEdit = false,
    dataView = false,
    changeDataIngredient = () => {},
    disable = true,
    setViewEnvironmentalData = () => {},
    viewEnvironmentalData = false
  } = props;

  const { nodeItemId, chartNodes, chartEdges, setChartNodes, saveDrawing, setNodeItem } =
    useContext(DrawingContext);

  const nodeItem = useMemo(() => getNode(nodeItemId || ""), [nodeItemId]);

  const [newSupplier, setNewSupplier] = useState(false);
  const [supplierData, setSupplierData] = useState<any>({});
  const [ingredientType, setIngredientType] = useState<string>("RAW");
  const [defaultType, setDefaultType] = useState<string>(DEFAULT_UNIT_TYPE);
  const [country, setCountry] = useState<number>(1);
  const [dataType, setDataType] = useState<string>("");
  const [supplierCountry, setSupplierCountry] = useState<number>(SUPPLIER_DEFAULT_COUNTRY_GLOBAL);
  const [isSupplierAsUser, setIsSupplierAsUser] = useState<boolean>(false);
  const [produceType, setProduceType] = useState<string>("");
  const [farmingType, setFarmingType] = useState<string>("Unknown");
  const [fieldType, setFieldType] = useState<string>("Unknown");
  const [specifyIngredientValue, setSpecifyIngredientValue] = useState<string>("");
  const [isSupplierExist, setIsSupplierExist] = useState<boolean>(false);

  const supplierAsUser = useMemo(() => {
    return (
      ingredientData?.supplierAsUser || supplierData?.supplier_data?.is_supplier_as_user || false
    );
  }, [ingredientData, supplierData]);

  const defaultUserDataType = useMemo(() => {
    if (supplierAsUser) {
      return supplierData?.supplier_data?.dataType || "SUPPLIER";
    }

    return supplierData?.supplier_data?.dataType || "USER";
  }, [supplierData, supplierAsUser]);

  useEffect(() => {
    setDataType(defaultUserDataType);
  }, [defaultUserDataType]);

  useEffect(() => {
    if (supplierData?.supplier_data?._id) {
      const isSupplierExist = ingredientData?.ingredient_data?.suppliers?.find(
        (supplier: any) => supplier?._id === supplierData?.supplier_data?._id
      );
      setIsSupplierExist(!!isSupplierExist);
    }
  }, [supplierData, ingredientData?.ingredient_data?.suppliers]);

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm();

  const newSupplierFormMethods = useForm();
  const {
    register: registerNewSupplier,
    reset: resetNewSupplier,
    handleSubmit: handleSubmitNewSupplier,
    control: controlNewSupplier,
    formState: { errors: errorsNewSupplier }
  } = newSupplierFormMethods;

  const {
    data: produceTypesData,
    isSuccess: isProduceTypesSuccess,
    isLoading: isProduceTypesLoading
  } = useGetProduceTypes();

  const closeSupplierData = () => {
    setNewSupplier(false);
    setIsSupplierAsUser(false);
    hideIngredientView(false);
    resetNewSupplier();
  };

  useImperativeHandle(ref, () => ({
    resetSupplierData() {
      closeSupplierData();
    },
    saveData: () => {
      if (newSupplier) {
        handleSubmitNewSupplier(handleSupplierSave)();
      } else {
        handleSubmit(saveData)();
      }
    }
  }));

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const countries = () => {
    if (isCountriesDataSuccess) {
      return countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id.toString()
      }));
    }
  };

  const { data: specifyIngredient } = useGetSpecifyIngredient();

  const produceTypes = useMemo(() => {
    if (isProduceTypesSuccess) {
      return produceTypesData?.map((p: any) => ({
        text: p.produce_name,
        key: p.id,
        value: p.id
      }));
    }
  }, [produceTypesData, isProduceTypesSuccess]);

  const { mutateAsync: mutatePostSupplier } = usePostSupplierData();
  const { mutateAsync: mutatePutSupplier } = usePutSupplierData();
  const { mutateAsync: mutatePutIngredient } = usePutIngredientData();
  const { mutate: mutatePostSupplierDrawingRequest } = useCreateSupplierDrawingRequest();

  let ingredient_id =
    ingredientData?.ingredient_data?.ingredient_id ||
    ingredientData?.ingredient_data?._id ||
    ingredientData?.ingredient_id;

  const resetSupplierData = () => {
    reset();
    setIngredientType("RAW");
    setDefaultType(DEFAULT_UNIT_TYPE);
    setCountry(countries()?.[0]?.value || "Denmark");
    setDataType("SUPPLIER");
    loadPackageData(false);
    setProduceType("");
    setFarmingType("Unknown");
    setFieldType("Unknown");
    setSpecifyIngredientValue("");
  };

  const loadSupplierEditData = useCallback(() => {
    if (disableEdit) {
      setValue("supplier_name", ingredientData?.supplier_name || "");
      saveSupplierEditData();
    }
  }, [ingredientData, disableEdit]);

  useEffect(() => {
    resetSupplierData();
    loadSupplierEditData();
  }, [ingredientData, loadSupplierEditData]);

  const saveSupplierEditData = () => {
    let supplierIngredientData = ingredientData?.supplierIngredientDetails;
    saveValues(ingredientData, supplierIngredientData);
  };

  const saveValues = (data: any, getSupplierIngredientDetails: any) => {
    let supplierTitle = data?.result?.title || data?.supplier_name;
    let supplierDetails =
      data?.result?.other?.supplierDetails || data?.result?.supplierDetails || data;
    let supplierDataType = getSupplierIngredientDetails?.data_type || "USER";
    let supplierData = {
      supplier_name: supplierTitle,
      supplier_data: { ...supplierDetails, dataType: supplierDataType }
    };
    setProduceType(getSupplierIngredientDetails?.produceType || "");
    setFarmingType(getSupplierIngredientDetails?.farmingType || "");
    setFieldType(getSupplierIngredientDetails?.fieldType || "");
    setSpecifyIngredientValue(getSupplierIngredientDetails?.specifyIngredient || "");
    supplierCurrentData(supplierData);
    setSupplierData(supplierData);
    setIngredientType(getSupplierIngredientDetails?.ingredient_type || "RAW");
    setDefaultType(getSupplierIngredientDetails?.unit_type || DEFAULT_UNIT_TYPE);
    setCountry(getSupplierIngredientDetails?.countryOrigin || "Denmark");
    setDataType(getSupplierIngredientDetails?.data_type || defaultUserDataType);
    setValue("unit_size", getSupplierIngredientDetails?.unit_size || 0);
    setValue("ean_bar_code", getSupplierIngredientDetails?.ean_bar_code || 0);
    setValue("produceCultivar", getSupplierIngredientDetails?.produceCultivar || 0);
    setSupplier(supplierData);
    loadPackageData(true);
  };

  const saveNodeData = () => {
    const updatedNode = {
      ...nodeItem,
      data: {
        ...nodeItem?.data,
        label: ingredientData.ingredient_data.ingredient_name,
        reference: {
          ingredientId: ingredientData.ingredient_data._id,
          supplierId: supplierData.supplier_data._id
        },
        description: `${ingredientData.ingredient_data.ingredient_name} ${supplierData.supplier_data.supplier_name}`
      }
    };
    const updatedChartNodes = chartNodes.map((n: any) => {
      if (n.id === nodeItem?.id) {
        return updatedNode;
      }
      return n;
    });
    setChartNodes(updatedChartNodes);
    setNodeItem(updatedNode);
    saveDrawing(chartEdges, updatedChartNodes);
  };

  // get supplier search data
  const getSearchValue = async (data: any) => {
    let supplierDetails = data?.result?.other?.supplierDetails || null;
    let supplierTitle = data?.result?.title || "";
    let supplierId = data?.result?.id || 0;

    if (supplierId === 0) {
      setNewSupplier(true);
      hideIngredientView(true);
      supplierCurrentData(undefined);
      setSupplierData({});
    } else {
      let checkNowItsAvailable = supplierDetails?.ingredientIds.some(
        (e: any) => e == ingredient_id
      );

      if (checkNowItsAvailable) {
        let getSupplierIngredientDetails = supplierDetails.supplierIngredientDetails.find(
          (e: any) => e.ingredient_id == ingredient_id
        );
        setNewSupplier(false);
        saveValues(data, getSupplierIngredientDetails);
      } else {
        let supplierData = {
          supplier_name: supplierTitle,
          supplier_data: supplierDetails
        };
        resetSupplierData();
        supplierCurrentData(supplierData);
        setSupplierData(supplierData);
      }
    }
  };

  // get supplier and ingredient ids
  const getIngredientAndSupplierId = (supplierData: any, ingredientId: any) => {
    let supplierProductId =
      supplierData?.supProductIds || supplierData?.productIds || supplierData?.supplierProductIds;
    let supplierIngredientData =
      supplierData?.supIngredientIds ||
      supplierData?.ingredientIds ||
      supplierData?.supplierIngredientIds;

    const newSupplierProductIds = supplierProductId;
    const newSupplierIngIds = supplierIngredientData?.includes(ingredientId)
      ? supplierIngredientData
      : [...supplierIngredientData, ingredientId];

    return {
      newSupplierProductIds,
      newSupplierIngIds
    };
  };

  // create new supplier drawing request
  const handleSupplierDrawingRequestSave = (ingredientId: string, supplierUserId: string) => {
    mutatePostSupplierDrawingRequest({
      ingredientId,
      supplierUserId
    });
  };

  // update supplier
  const saveData = (data: any) => {
    if (dataType === IngredientDataType.GENERIC) {
      if (ingredientType === IngredientType.PROCESSED && isEmpty(specifyIngredientValue)) {
        errorView("Please Specify Ingredient");
        return;
      }
      if (ingredientType === IngredientType.RAW && isEmpty(produceType)) {
        errorView("Please select Produce Type");
        return;
      }
    }

    const { newSupplierProductIds, newSupplierIngIds } = getIngredientAndSupplierId(
      supplierData?.supplier_data,
      ingredient_id
    );

    let supplierId =
      supplierData?.supplier_data?.supplier_id ||
      supplierData?.supplier_data?._id ||
      supplierData?.supplier_id;

    let supplierIngredientDetails = Object.assign(
      {
        ingredient_id: ingredient_id,
        unit_type: defaultType,
        unit_size: data.unit_size,
        ean_bar_code: data.ean_bar_code
      },
      viewEnvironmentalData
        ? {
            data_type: dataType,
            ingredient_type: ingredientType,
            countryOrigin: country,
            specifyIngredient: specifyIngredientValue,
            produceType: produceType,
            produceCultivar: data.produceCultivar,
            farmingType: farmingType,
            fieldType: fieldType
          }
        : {
            data_type: "",
            ingredient_type: "",
            countryOrigin: 0,
            specifyIngredient: "",
            produceType: "",
            produceCultivar: "",
            farmingType: "",
            fieldType: ""
          }
    );

    let supplierDataUpdate = {
      _id: supplierId,
      supplier_name: supplierData?.supplier_name,
      productIds: newSupplierProductIds ? newSupplierProductIds : [],
      ingredientIds: newSupplierIngIds ? newSupplierIngIds : [],
      supplierIngredientDetails: supplierIngredientDetails
    };

    const updateIngredientData = {
      _id: ingredient_id,
      ingredient_name:
        ingredientData?.ingredient_data?.ingredient_name || ingredientData?.ingredient_name,
      productIds:
        ingredientData?.ingredient_data?.productIds || ingredientData?.ingredientProductIds,
      ingredient_source: ingredientData.ingredient_data.ingredient_source
    };

    mutatePutIngredient(updateIngredientData, {
      onSuccess(data: any) {
        successMessage("Ingredient update successfully");
        let ingredientData = {
          ingredient_name: data?.ingredient_name,
          ingredient_data: data
        };
        setIngredientData(ingredientData);
        mutatePutSupplier(supplierDataUpdate, {
          onSuccess(data: any) {
            let supplierIngredient = data.supplierIngredientDetails;
            successMessage("Supplier update successfully");
            saveValues(data, supplierIngredient);
            saveNodeData();
            refetchSupplier();
            if (data.is_supplier_as_user && dataType === "SUPPLIER") {
              const supplierUserId = data.user_id;
              handleSupplierDrawingRequestSave(ingredient_id, supplierUserId);
            }
          }
        });
      }
    });
  };

  const handleSearchValidation = (status: any) => {
    if (status) {
      resetSupplierData();
    }
  };

  //create new supplier
  const handleSupplierSave = (data: any) => {
    if (dataType === IngredientDataType.GENERIC) {
      if (ingredientType === IngredientType.PROCESSED && isEmpty(specifyIngredientValue)) {
        errorView("Please Specify Ingredient");
        return;
      }
      if (ingredientType === IngredientType.RAW && isEmpty(produceType)) {
        errorView("Please select Produce Type");
        return;
      }
    }

    const dataToSubmit = {
      ...data,
      supplier_name: trim(data.supplier_name),
      supplier_contact_name: trim(data.supplier_contact_name),
      supplier_email: trim(data.supplier_email),
      supplier_country: supplierCountry,
      is_supplier_as_user: isSupplierAsUser,
      productIds: [],
      ingredientIds: [ingredient_id],
      theme: THEME
    };
    mutatePostSupplier(dataToSubmit, {
      onSuccess(data: any) {
        let supplierData = {
          supplier_name: data.supplier_name,
          supplier_data: data
        };
        resetNewSupplier();
        supplierCurrentData(supplierData);
        setSupplierData(supplierData);
        successMessage("Supplier saved successfully");
        setNewSupplier(false);
        refetchSupplier();
        hideIngredientView(false);
        setIsSupplierAsUser(false);
        if (data.is_supplier_as_user && dataType === IngredientDataType.SUPPLIER) {
          const supplierUserId = data.user_id;
          handleSupplierDrawingRequestSave(ingredient_id, supplierUserId);
        }
      }
    });
  };

  const checkRawOrProcess = useMemo(() => {
    if (ingredientType == IngredientType.PROCESSED && dataType == IngredientDataType.GENERIC) {
      return {
        processIngredient: true,
        produceIngredient: false,
        enableIngredient: false
      };
    }

    if (ingredientType == IngredientType.RAW && dataType == IngredientDataType.GENERIC) {
      return {
        processIngredient: false,
        produceIngredient: true,
        enableIngredient: true
      };
    }

    return {
      processIngredient: false,
      produceIngredient: false,
      enableIngredient: false
    };
  }, [ingredientType, dataType]);

  useEffect(() => {
    changeDataIngredient(checkRawOrProcess);
  }, [checkRawOrProcess, changeDataIngredient]);

  return (
    <>
      {newSupplier ? (
        ENABLE_LCA_SUPPLIER_UI_REDESIGN ? (
          <FormProvider {...newSupplierFormMethods}>
            <SupplierRegisterFields
              control={controlNewSupplier}
              country={supplierCountry}
              onCountryChange={setSupplierCountry}
              isSupplierAsUser={isSupplierAsUser}
              onIsSupplierAsUserChange={setIsSupplierAsUser}
              showRegisterAsUserCheckbox={false}
            />
          </FormProvider>
        ) : (
          <SupplierRegisterForm
            handleSubmit={handleSubmitNewSupplier}
            onSubmit={handleSupplierSave}
            register={registerNewSupplier}
            errors={errorsNewSupplier}
            setCountry={setSupplierCountry}
            country={supplierCountry}
            setIsSupplierAsUser={setIsSupplierAsUser}
            isSupplierAsUser={isSupplierAsUser}
            hideSupplierContent={false}
          />
        )
      ) : (
        <SupplerIngredientDataView
          dataView={dataView}
          supplierAsUser={supplierAsUser}
          register={register}
          ingredientType={ingredientType}
          setIngredientType={setIngredientType}
          dataType={dataType}
          errors={errors}
          country={country}
          countries={() => countries()}
          setCountry={setCountry}
          isCountriesDataLoading={isCountriesDataLoading}
          defaultType={defaultType}
          setDefaultType={setDefaultType}
          setDataType={setDataType}
          produceType={produceType}
          produceTypes={produceTypes}
          setProduceType={setProduceType}
          isProduceTypesLoading={isProduceTypesLoading}
          farmingType={farmingType}
          fieldType={fieldType}
          setFarmingType={setFarmingType}
          setFieldType={setFieldType}
          disableEdit={disableEdit}
          errorsNewSupplier={errorsNewSupplier}
          ingredientSupplierList={ingredientSupplierList}
          supplierData={supplierData}
          handleSearchValidation={handleSearchValidation}
          getSearchValue={getSearchValue}
          checkRawOrProcess={checkRawOrProcess}
          specifyIngredient={specifyIngredient}
          specifyIngredientValue={specifyIngredientValue}
          setSpecifyIngredientValue={setSpecifyIngredientValue}
          disable={disable}
          setViewEnvironmentalData={setViewEnvironmentalData}
          viewEnvironmentalData={viewEnvironmentalData}
        />
      )}
    </>
  );
});

export default SupplerRegister;
